import { useEffect, useMemo } from 'react'
import * as THREE from 'three'
import { deslocamentoNoPonto } from '../../dominio/didatico/campoDeformacao'
import type { PerfilVeiculo } from '../../dominio/didatico/perfilVeiculo'
import { useDeformacao } from './contextoDeformacao'

/**
 * Riscos do abalroamento.
 *
 * No contato tangencial a chapa quase não entra: o que conta a história são os
 * riscos longos no sentido do arrasto, na altura em que as duas laterais se
 * tocaram. As linhas seguem o amassado (consultam o mesmo campo) e aparecem
 * junto com ele — nada antes do contato, e crescem com o esmagamento.
 */
export function DanoRiscos({ perfil }: { perfil: PerfilVeiculo }) {
  const ctx = useDeformacao()
  const chave = ctx?.estado.chave ?? ''
  const geometria = useMemo(() => {
    if (!ctx) return null
    const campos = ctx.estado.campos.filter((c) => c.riscos && (c.regiao === 'lateralEsquerda' || c.regiao === 'lateralDireita'))
    if (campos.length === 0) return null
    const posicoes: number[] = []
    const hw = perfil.largura / 2
    for (const campo of campos) {
      const comprimento = campo.larguraM * campo.progresso
      const z0 = campo.centroFaceM - comprimento / 2
      // cinco riscos, com inclinação e altura levemente diferentes (determinísticas)
      for (let r = 0; r < 5; r += 1) {
        const y0 = campo.alturaContatoY - 0.1 + r * 0.06
        const inclinacao = (((r * 37) % 7) - 3) * 0.012
        const passos = 24
        let anterior: THREE.Vector3 | null = null
        for (let i = 0; i <= passos; i += 1) {
          const z = z0 + (comprimento * i) / passos
          const y = y0 + inclinacao * (z - z0)
          const base = { x: campo.sinalLateral * (hw + 0.012), y, z }
          const d = deslocamentoNoPonto(ctx.estado, perfil, base, 'porta')
          const ponto = new THREE.Vector3(base.x + d.x, base.y + d.y, base.z + d.z)
          if (anterior) posicoes.push(anterior.x, anterior.y, anterior.z, ponto.x, ponto.y, ponto.z)
          anterior = ponto
        }
      }
    }
    const g = new THREE.BufferGeometry()
    g.setAttribute('position', new THREE.Float32BufferAttribute(posicoes, 3))
    return g
  }, [chave, perfil])
  useEffect(() => () => geometria?.dispose(), [geometria])
  if (!geometria) return null
  return (
    <lineSegments geometry={geometria} renderOrder={6}>
      <lineBasicMaterial color="#e2e8f0" transparent opacity={0.9} depthWrite={false} />
    </lineSegments>
  )
}
